import { useEffect, useRef } from "react";

// Decreasing sizes for the drops that trail behind the pointer, largest
// (the "core") first.
const RATIOS = [1, 0.9, 0.82, 0.76, 0.7];

type Drop = {
  el: HTMLDivElement;
  r: number;
  x: number;
  y: number;
  s: number;
  ph: number;
  w: number;
};

/**
 * Home page background: four breathing colour glows (independently drifting
 * and swelling so the motion never visibly loops) plus a "honey" cursor - a
 * soft blob of merged drops that chases the pointer with viscous lag. Both
 * effects are purely decorative (aria-hidden) and skipped entirely when the
 * user prefers reduced motion.
 */
export function AtmosBackground() {
  const atmosRef = useRef<HTMLDivElement>(null);
  const honeyRef = useRef<HTMLDivElement>(null);
  const dropElRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const atmos = atmosRef.current;
    const honey = honeyRef.current;
    if (!atmos || !honey) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const drops: Drop[] = RATIOS.map((r, i) => {
      const el = dropElRefs.current[i];
      if (!el) return null;
      return { el, r, x: 0, y: 0, s: 0, ph: i * 1.7, w: 0.8 + i * 0.21 };
    }).filter((d): d is Drop => d !== null);

    if (drops.length === 0) return;

    let core = 0;
    let box = 0;

    function size() {
      core = Math.max(
        40,
        Math.min(84, Math.min(window.innerWidth, window.innerHeight) * 0.09),
      );
      box = Math.round(core * 3);
      honey!.style.width = `${box}px`;
      honey!.style.height = `${box}px`;
      drops.forEach((d) => {
        d.s = core * d.r;
        d.el.style.width = `${d.s}px`;
        d.el.style.height = `${d.s}px`;
      });
    }
    size();
    window.addEventListener("resize", size);

    let tx = 0;
    let ty = 0;
    let raf = 0;
    let seen = false;
    let hideT: ReturnType<typeof setTimeout> | undefined;
    let present = false;
    let presence = 0;
    let energy = 0;
    let boost = 0;
    const t0 = performance.now();

    function tick(now: number) {
      const t = (now - t0) / 1000;
      const h = drops[0];
      const px = h.x;
      const py = h.y;
      h.x += (tx - h.x) * 0.08;
      h.y += (ty - h.y) * 0.08;
      const amp = core * 0.14;
      const tether = core * 0.42;
      for (let i = 1; i < drops.length; i++) {
        const d = drops[i];
        const p = drops[i - 1];
        const gx = p.x + Math.cos(t * d.w + d.ph) * amp;
        const gy = p.y + Math.sin(t * d.w * 1.3 + d.ph) * amp;
        d.x += (gx - d.x) * 0.3;
        d.y += (gy - d.y) * 0.3;
        const dx = d.x - h.x;
        const dy = d.y - h.y;
        const dist = Math.hypot(dx, dy);
        if (dist > tether) {
          d.x = h.x + (dx / dist) * tether;
          d.y = h.y + (dy / dist) * tether;
        }
      }
      // brighter while flowing, a faint blob at rest
      energy +=
        (Math.min(Math.hypot(h.x - px, h.y - py) / 12, 1) - energy) * 0.1;
      presence += ((present ? 1 : 0) - presence) * 0.08;
      boost *= 0.9;
      const k = (1 + boost).toFixed(3);
      const half = box / 2;
      honey!.style.transform = `translate3d(${h.x - half}px,${h.y - half}px,0)`;
      for (const d of drops) {
        d.el.style.transform = `translate3d(${d.x - h.x + half - d.s / 2}px,${d.y - h.y + half - d.s / 2}px,0) scale(${k})`;
      }
      honey!.style.opacity = (
        presence *
        (0.1 + energy * 0.4 + boost * 0.4)
      ).toFixed(3);
      raf = !present && presence < 0.005 ? 0 : requestAnimationFrame(tick);
    }

    function wake() {
      if (!raf) raf = requestAnimationFrame(tick);
    }

    function aim(e: PointerEvent) {
      const r = atmos!.getBoundingClientRect();
      tx = e.clientX - r.left;
      ty = e.clientY - r.top;
      if (!seen) {
        drops.forEach((d) => {
          d.x = tx;
          d.y = ty;
        });
        seen = true;
      }
      present = true;
      clearTimeout(hideT);
      wake();
    }

    function leave() {
      present = false;
      wake();
    }

    function lift(e: PointerEvent) {
      if (e.pointerType !== "mouse") hideT = setTimeout(leave, 900);
    }

    function down(e: PointerEvent) {
      aim(e);
      boost = 0.18;
    }

    window.addEventListener("pointermove", aim, { passive: true });
    window.addEventListener("pointerdown", down);
    window.addEventListener("pointerup", lift);
    window.addEventListener("pointercancel", lift);
    document.documentElement.addEventListener("mouseleave", leave);

    return () => {
      window.removeEventListener("resize", size);
      window.removeEventListener("pointermove", aim);
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointerup", lift);
      window.removeEventListener("pointercancel", lift);
      document.documentElement.removeEventListener("mouseleave", leave);
      clearTimeout(hideT);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="atmos" aria-hidden="true" ref={atmosRef}>
      <div className="glow amber" />
      <div className="glow coral" />
      <div className="glow blue" />
      <div className="glow bloom" />
      <div className="honey" ref={honeyRef}>
        {RATIOS.map((_, i) => (
          <div
            key={i}
            className="drop"
            ref={(el) => {
              dropElRefs.current[i] = el;
            }}
          />
        ))}
      </div>
      <svg className="goo-defs" aria-hidden="true">
        <defs>
          <filter id="goo" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 16 -6"
              result="goo"
            />
            <feBlend in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>
    </div>
  );
}
